import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, afterRenderEffect, computed, inject, input, linkedSignal, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { AssignableDiagnosisView, CASE_DELETE_REASONS, CaseStatus, CaseView, CodingModel, caseCoversDate } from '@lib/guarantees/data-access';
import {
  addCaseComment,
  assignDiagnosisToCase,
  closeCase,
  deleteCase,
  reopenCase,
  restoreCase,
  unassignDiagnosisFromCase,
} from '@lib/guarantees/data-access/store/guarantees-v2.actions';
import { selectAssignableDiagnoses, selectCasesError, selectCasesLoading, selectGuaranteesSaving } from '@lib/guarantees/data-access/store/guarantees-v2.selectors';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';

import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuContent, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';

import { openCaseForm } from '../../operations/case-form.component';
import { openConfirm, openDeleteConfirm } from '../../operations/confirm-dialog.component';
import { CalloutComponent } from '../common/callout.component';
import { ChipComponent } from '../common/chip.component';
import { CodingChipComponent } from '../common/coding-chip.component';
import { ControlDirective } from '../common/control.directive';
import { diagnosisLabelOf } from '../common/diagnosis-label';
import { FactComponent } from '../common/fact.component';
import { ChipTone } from '../common/guarantee-vocabulary';
import { PanelSectionComponent } from '../common/panel-section.component';
import { SpinnerComponent } from '../common/spinner.component';
import { BUTTON_CLASSES, ICON_BUTTON_CLASS, ICON_BUTTON_DANGER_CLASS, MENU_CLASSES } from '../ui/ui.classes';

const STATUS_TONES: Record<CaseStatus, ChipTone> = {
  Active: 'success',
  Expiring: 'warning',
  Expired: 'error',
  Closed: 'neutral',
  Deleted: 'neutral',
};

/**
 * How the countdown beside the span is drawn. By status rather than by the sign of the number: every
 * case now carries an expiry, so painting anything with days left as a warning would make a healthy
 * list read as a wall of amber and leave the two states that do want attention indistinguishable.
 */
const EXPIRY_CLASS: Record<CaseStatus, string> = {
  Active: 'text-neutral-400',
  Expiring: 'text-warning-700',
  Expired: 'text-error-600',
  Closed: 'text-neutral-400',
  Deleted: 'text-neutral-400',
};

const STATUS_ICONS: Record<CaseStatus, string> = {
  Active: 'play_circle',
  Expiring: 'hourglass_bottom',
  Expired: 'event_busy',
  Closed: 'check_circle',
  Deleted: 'delete',
};

/**
 * The member's insurance cases and their state. Cases are entirely manual, so this is the only
 * place one is opened, closed, given a span or retired — nothing on the backend does it.
 *
 * Reading and editing are kept apart. A row expands into the whole case as it stands; changing it
 * is a menu act that opens the form, so unfolding a case can never be the first step of altering
 * one. The acts that are not amendments — filing a guarantee, unfiling one, leaving a comment —
 * stay in the expanded view, beside the thing they act on.
 *
 * Sits in the page's side panel, which supplies its frame and its title.
 */
@Component({
  selector: 'guarantees-case-panel',
  standalone: true,
  imports: [
    DatePipe,
    FormsModule,
    MatIcon,
    MatMenu,
    MatMenuContent,
    MatMenuItem,
    MatMenuTrigger,
    MatTooltip,
    TranslatePipe,
    CalloutComponent,
    ChipComponent,
    CodingChipComponent,
    ControlDirective,
    FactComponent,
    PanelSectionComponent,
    SpinnerComponent,
  ],
  templateUrl: './case-panel.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CasePanelComponent {
  /** Dropdown chrome, so a menu opened from a row reads as part of the same page. */
  protected readonly menu = MENU_CLASSES;

  /** The module's button vocabulary — nothing here rolls a pill of its own. */
  protected readonly buttons = BUTTON_CLASSES;

  /** Named for the template, which has no other way to say which state a row is in. */
  protected readonly statuses = CaseStatus;

  cases = input.required<CaseView[]>();

  /** Arrives expanded and scrolled to: the panel was opened to read this one case. */
  focusCaseId = input<string | null>(null);

  /** Withholds every control that writes, for a portal that may read the record but not change it. */
  readOnly = input<boolean>(false);

  /** Jumping to a guarantee a case holds — the panel does not own navigation. */
  guaranteeSelected = output<string>();

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly store = inject(Store);
  private readonly dialog = inject(MatDialog);

  /** Always visible: on an editing panel these are the point of the row, not incidental. */
  protected readonly menuButtonClass = ICON_BUTTON_CLASS;

  /** The same shape, tinted for the one act in the panel that takes something out of the case. */
  protected readonly unlinkButtonClass = ICON_BUTTON_DANGER_CLASS;

  /** Every diagnosis in the window, so a guarantee can be filed without leaving the panel. */
  private readonly assignable = this.store.selectSignal(selectAssignableDiagnoses);

  protected readonly loading = this.store.selectSignal(selectCasesLoading);
  protected readonly error = this.store.selectSignal(selectCasesError);
  protected readonly saving = this.store.selectSignal(selectGuaranteesSaving);

  /** Follows what the panel was opened onto, until a row here is toggled on its own. */
  protected readonly expandedId = linkedSignal<string | null>(() => this.focusCaseId());

  /** Cases still in play — the panel's default content. */
  protected readonly openCount = computed(() => this.cases().filter(view => this.isOpen(view)).length);

  /** Shown when they are all there is, or the panel opens onto nothing and claims there are none. */
  protected readonly showClosed = linkedSignal(() => this.cases().length > 0 && this.openCount() === 0);

  protected readonly commentDrafts = signal<Record<string, string>>({});

  protected readonly visible = computed(() => (this.showClosed() ? this.cases() : this.cases().filter(view => this.isOpen(view))));

  protected readonly closedCount = computed(() => this.cases().length - this.openCount());

  protected readonly attentionCount = computed(() => this.cases().filter(view => view.status === CaseStatus.Expiring || view.status === CaseStatus.Expired).length);

  /** Neither closed nor retired — the two states that take a case out of the panel's default list. */
  private isOpen(view: CaseView): boolean {
    return view.status !== CaseStatus.Closed && view.status !== CaseStatus.Deleted;
  }

  protected tone(status: CaseStatus): ChipTone {
    return STATUS_TONES[status] ?? 'neutral';
  }

  protected icon(status: CaseStatus): string {
    return STATUS_ICONS[status] ?? 'folder';
  }

  protected expiryClass(status: CaseStatus): string {
    return EXPIRY_CLASS[status] ?? 'text-neutral-400';
  }

  protected isExpanded(caseId: string): boolean {
    return this.expandedId() === caseId;
  }

  protected toggle(caseId: string): void {
    this.expandedId.update(current => (current === caseId ? null : caseId));
  }

  protected toggleClosed(): void {
    this.showClosed.update(value => !value);
  }

  /** Opening one, and amending one. The same form both times — a case is written as a whole. */
  protected create(): void {
    openCaseForm(this.dialog, this.store);
  }

  protected edit(view: CaseView): void {
    openCaseForm(this.dialog, this.store, { view });
  }

  /** The curated set, as labels: the codes are read here and changed in the form. */
  protected codingsOf(view: CaseView): CodingModel[] {
    const byCode = new Map<string, CodingModel>();

    for (const entry of view.case.diagnoses ?? []) {
      if (entry.icd10?.code && !byCode.has(entry.icd10.code)) {
        byCode.set(entry.icd10.code, entry.icd10);
      }
    }

    return [...byCode.values()];
  }

  /**
   * What this case could still take in: a diagnosis in the loaded window, not already filed here,
   * on a guarantee the case's span admits. The span is the rule — a case holds the care given
   * while it ran — and it is applied here as well as in the stream, so neither surface offers a
   * filing the other would refuse.
   */
  protected candidates(view: CaseView): AssignableDiagnosisView[] {
    return this.assignable().filter(entry => entry.diagnosis.caseId !== view.case.id && caseCoversDate(view.case, entry.visitDate));
  }

  protected diagnosisLabel(entry: AssignableDiagnosisView): string {
    return diagnosisLabelOf(entry.diagnosis);
  }

  /** Filing a guarantee's diagnosis under this case. Filing is scoped to the guarantee that owns it. */
  protected assignDiagnosis(view: CaseView, entry: AssignableDiagnosisView): void {
    this.store.dispatch(assignDiagnosisToCase({ caseId: view.case.id, guaranteeId: entry.guaranteeId, diagnosisId: entry.diagnosis.id }));
  }

  protected unassignDiagnosis(view: CaseView, guaranteeId: string, diagnosisId: string): void {
    this.store.dispatch(unassignDiagnosisFromCase({ caseId: view.case.id, guaranteeId, diagnosisId }));
  }

  /** How a diagnosis reads on an unfiling control, so two of them on one guarantee are told apart. */
  protected codingLabel(coding?: CodingModel): string {
    return [coding?.code, coding?.name].filter(Boolean).join(' ');
  }

  /** What the question is about — the case's own name, or its codes where it was never named. */
  private subjectOf(view: CaseView): string {
    return view.case.code?.trim() || view.codes.join(', ');
  }

  /**
   * Closing ends the care story the case tells. Still asked first, though it is no longer final:
   * a closure takes the case out of the working list, and a manager who did not mean to close it
   * would have to think to look under «Показати закриті» to find it again. No ground is owed —
   * it is a completion, not a retraction.
   */
  protected close(view: CaseView): void {
    openConfirm(this.dialog, {
      subject: this.subjectOf(view),
      titleKey: 'guaranteeV2.action.closeCase',
      warningKey: 'guaranteeV2.message.closeCaseWarning',
      confirmKey: 'guaranteeV2.action.closeCase',
      icon: 'check_circle',
      tone: 'success',
    }).subscribe(result => {
      if (result) {
        this.store.dispatch(closeCase({ caseId: view.case.id, endedAt: new Date().toISOString() }));
      }
    });
  }

  /** Retiring the case, asked the way every deletion in the module is — with the ground recorded. */
  protected remove(view: CaseView): void {
    openDeleteConfirm(this.dialog, {
      subject: this.subjectOf(view),
      titleKey: 'guaranteeV2.action.deleteCase',
      warningKey: 'guaranteeV2.message.deleteCaseWarning',
      reasons: CASE_DELETE_REASONS,
    }).subscribe(result => {
      if (result) {
        this.store.dispatch(deleteCase({ caseId: view.case.id, reason: result.reason }));
      }
    });
  }

  protected restore(view: CaseView): void {
    this.store.dispatch(restoreCase({ caseId: view.case.id }));
  }

  /**
   * Undoing a closure. Unasked, unlike the closure itself: closing was confirmed because nothing
   * could take it back, and this is what takes it back — pressing close again undoes it in turn.
   */
  protected reopen(view: CaseView): void {
    this.store.dispatch(reopenCase({ caseId: view.case.id }));
  }

  /**
   * Whether reopening this case would hand back an expired one. The span is not touched by a
   * closure, so a case closed in March and reopened in September comes back already out of time —
   * said on the row, because the alternative is finding out from the status chip afterwards.
   */
  protected reopensExpired(view: CaseView): boolean {
    return view.daysToExpiry !== undefined && view.daysToExpiry < 0;
  }

  protected commentDraft(caseId: string): string {
    return this.commentDrafts()[caseId] ?? '';
  }

  protected setCommentDraft(caseId: string, text: string): void {
    this.commentDrafts.update(drafts => ({ ...drafts, [caseId]: text }));
  }

  protected submitComment(view: CaseView): void {
    const text = this.commentDraft(view.case.id).trim();
    if (!text) {
      return;
    }

    this.store.dispatch(addCaseComment({ caseId: view.case.id, text }));
    this.setCommentDraft(view.case.id, '');
  }

  constructor() {
    // After render, not during it: the panel and the row it is being opened onto are built by the
    // same change, and scrolling to a row that has not been laid out yet aims at nothing.
    afterRenderEffect(() => {
      const caseId = this.focusCaseId();
      if (caseId) {
        this.host.nativeElement.querySelector(`[data-case-id="${caseId}"]`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    });
  }
}
